/**
 * The body-portal stacking ladder.
 *
 * Every fixed overlay that portals to document.body is a sibling of every
 * other one, so only these numbers decide who paints on top — the React
 * tree that opened it is irrelevant. Anything newly portaled to body must
 * take a rung from here, never a "dropdown-ish" 9000/10000: three tickets
 * in one week (template editor, settings strips, View Prompt) were exactly
 * that — a modal that looked fine inline, then opened UNDER the item modal
 * once it moved to a portal.
 *
 * Read it bottom-up: an item modal is the floor everything else must clear.
 * Values are the ones the app already shipped with; this file just names
 * them so the next portal lands on the ladder by construction.
 */
export const Z = {
  /** The tended card opened over a page — TendedItemModal (Tended/Published
   *  lists, ?item= deep links) and the ContentCard / TendButton /
   *  TendedContentModal tended views. Same rung for all: nothing is meant
   *  to sit between two item modals. */
  ITEM_MODAL: 999999,
  /** Non-modal surfaces that ride above an item modal: the draggable
   *  preview panel, click-catcher backdrops behind placement menus. */
  ABOVE_ITEM: 1000000,
  /** Modal dialogs that open from inside an item modal: the TendModal
   *  shell (View Prompt, upload/edit logo, RSS dialogs…), TacticEditModal,
   *  TemplateManagementModal. */
  EDITOR_MODAL: 1000003,
  /** Settings strips popped out of an editor toolbar (background / border). */
  STRIP: 1000004,
  /** Anchored menus — the publish menu, the channel link menu. */
  MENU: 1000010,
  /** Confirmation veils over a menu (SmartPublish pre-publish warnings). */
  WARNINGS: 1000020,
  /** The anchored feedback panel — comments must be reachable from inside
   *  any modal, so it clears the whole modal band. */
  FEEDBACK: 1000030,
  /** Pickers (color, font, text style, element design, logo) — top of the
   *  world, they nest inside everything above. */
  PICKER: 2147483647,
} as const;

export type ZLayer = (typeof Z)[keyof typeof Z];
